import { useEffect, useState } from 'react';

import { DEFAULT_ERROR, useCustomSnackBar } from 'hooks/useCustomSnackBar';

import { useMutationSetBaseLineDateOption } from 'api/mutations/setBaseLineDateOption.mutation';
import { useQueryGetBaseLineDateOption } from 'api/queries/getBaseLineDateOption.query';

import {
    TDefaultBaseLinePerVersion,
    defaultBaseLinePerVersionList,
    findValue,
} from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/ProjectPreferences/ProjectPreferences.utils';
import { Project } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/ProjectPreferences/ProjectPreferencesTypes';
import { Select } from 'components/DesignSystem/Select';
import { ConfirmSaveModal } from 'components/common';

export const DefaultBaseLinePerVersion = ({ project }: { project: Project }) => {
    const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
    const [selectedValue, setSelectedValue] = useState<ISelectOption<TDefaultBaseLinePerVersion> | null>(null);
    const { handleSnackBar } = useCustomSnackBar();
    const mutationBaseLineDateOption = useMutationSetBaseLineDateOption();
    const { data: defaultBaseLinePerVersion, refetch } = useQueryGetBaseLineDateOption({
        projectId: project?.id,
    });

    useEffect(() => {
        if (defaultBaseLinePerVersion) {
            setSelectedValue(findValue(defaultBaseLinePerVersion));
        }
    }, [defaultBaseLinePerVersion]);

    const handleDefaultBaseLinePerVersion = (value: ISelectOption<TDefaultBaseLinePerVersion> | null) => {
        if (value) {
            if (value.value !== selectedValue?.value) {
                setShowConfirmModal(true);
                setSelectedValue(value);
            }
        }
    };

    const handleHideModal = () => {
        setShowConfirmModal(false);
        if (defaultBaseLinePerVersion !== undefined) {
            setSelectedValue(findValue(defaultBaseLinePerVersion));
        }
    };

    const handleSave = () => {
        setShowConfirmModal(false);
        mutationBaseLineDateOption.mutate(
            { projectId: project?.id, option: selectedValue?.value },
            {
                onSuccess: () => handleSnackBar('Default Baseline per Version has changed', 'success'),
                onError: (error) => {
                    DEFAULT_ERROR({ error, handleError: handleSnackBar });
                },
                onSettled: () => refetch(),
            },
        );
    };

    return (
        <>
            <span>Default Baseline per Version:</span>
            <Select<ISelectOption<TDefaultBaseLinePerVersion>>
                id={'project-preferences-default-compared-version'}
                value={selectedValue}
                options={defaultBaseLinePerVersionList}
                onChange={handleDefaultBaseLinePerVersion}
                isSearchable={false}
            />
            <ConfirmSaveModal
                isShowModal={showConfirmModal}
                onHideModal={handleHideModal}
                onConfirm={handleSave}
                isLoading={mutationBaseLineDateOption.isLoading}
            >
                You have made changes. Would you like to save them?
            </ConfirmSaveModal>
        </>
    );
};
